import { useEffect, useMemo, useRef, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Pickaxe, Plus, Pencil, Trash2, Upload, Download, X } from "lucide-react"
import { api } from "@/app/api/client"
import { useClientTheme } from "@/app/components/ThemeContext"
import {
  Field,
  Input,
  ErrorBanner,
  SuccessBanner,
  labelStyle,
  formatApiError,
} from "@/app/components/FormPrimitives"
import { PageHeader } from "@/app/components/PageHeader"
import { FilterBar } from "@/app/components/FilterBar/FilterBar"
import { SearchInput } from "@/app/components/FilterBar/SearchInput"
import { TableColumnHeader } from "@/app/components/TableColumnHeader"
import { TableEmptyState } from "@/app/components/TableEmptyState"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { useIsMobile } from "@/app/hooks/useIsMobile"
import { DataCard } from "@/app/components/DataCard"
import { StrDetailModal } from "@/app/components/StrDetailModal"
import { normalizeStr } from "@/app/lib/str"
import { ACQ_GREEN } from "@/app/lib/txColors"
import { toCsv, downloadCsv } from "@/app/lib/csv"
import { primaryButton, secondaryButton } from "@/app/lib/buttonStyles"
import { stickyTh } from "@/app/lib/tableStyles"
import type { InventoryRow } from "@/app/components/InventoryTable"
import type { ClientTheme } from "@/app/lib/themes"

interface Well {
  id: string
  well_name: string
  operator: string | null
  /** Canonical display name (shortened + variant-grouped); fall back to `operator`. */
  operator_display?: string | null
  interests: string[]
  wi: string | number | null
  ri: string | number | null
  strs: string[]
}

interface WellsResponse {
  data: Well[]
  meta?: unknown
}

interface ImportSummary {
  created: number
  updated: number
  errored: { well_name: string | null; reason: string }[]
}

const INTEREST_OPTIONS = ["WI", "RI", "ORRI"]

const blankForm = {
  well_name: "",
  operator: "",
  strs: "",
  wi: "",
  ri: "",
  interests: [] as string[],
}

type FormState = typeof blankForm

export function UnitsPage() {
  const { theme } = useClientTheme()
  const { user, canWrite, isLoading: userLoading } = useCurrentUser()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(blankForm)
  const [search, setSearch] = useState("")
  const [validationError, setValidationError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const importInputRef = useRef<HTMLInputElement>(null)

  const [activeStrRow, setActiveStrRow] = useState<InventoryRow | null>(null)

  const { data, isLoading } = useQuery<WellsResponse>({
    queryKey: ["wells"],
    queryFn: () => api.get<WellsResponse>("/wells?per_page=2000"),
  })

  // Inventory rows (cached app-wide) back the STR summary popout opened from a
  // well's STR chips.
  const { data: inventory } = useQuery<{ data: InventoryRow[] }>({
    queryKey: ["inventories", "all"],
    queryFn: () => api.get<{ data: InventoryRow[] }>("/inventories?per_page=2000"),
  })

  const wells = data?.data ?? []

  // STRs currently held in Master Inventory — their chips render green.
  const inventoryStrs = useMemo(() => {
    const set = new Set<string>()
    for (const r of inventory?.data ?? []) {
      const n = normalizeStr(r.str)
      if (n) set.add(n)
    }
    return set
  }, [inventory])

  // Open the STR summary for a well's STR — use the live inventory row when
  // present, otherwise a minimal fallback (the modal fetches its own details).
  function openStr(str: string) {
    const target = normalizeStr(str)
    const row = inventory?.data.find((r) => normalizeStr(r.str) === target)
    if (row) return setActiveStrRow(row)

    const canonical = target ?? str
    const parts = canonical.split("-")
    setActiveStrRow({
      id: canonical,
      str: canonical,
      section: parts[0] ? Number(parts[0]) : null,
      township_range: parts.slice(1).join("-"),
      county: null,
      operator: null,
      net_acres: "0",
      weighted_nri: "0",
      orri_value: "0",
      wells: [],
      interest_types: [],
      note: null,
    })
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return wells
    return wells.filter(
      (w) =>
        w.well_name?.toLowerCase().includes(q) ||
        w.operator?.toLowerCase().includes(q) ||
        w.operator_display?.toLowerCase().includes(q) ||
        w.strs?.some((s) => s.toLowerCase().includes(q))
    )
  }, [wells, search])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["wells"] })
    queryClient.invalidateQueries({ queryKey: ["inventories"] })
  }

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editingId ? api.patch(`/wells/${editingId}`, payload) : api.post("/wells", payload),
    onSuccess: () => {
      invalidate()
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.del(`/wells/${id}`),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(null)
    },
  })

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const text = await file.text()
      return api.post<ImportSummary>("/wells/import", { csv: text })
    },
    onSuccess: () => invalidate(),
  })

  function handleImportFile(file: File | undefined | null) {
    if (!file) return
    importMutation.reset()
    importMutation.mutate(file)
  }

  function handleExport() {
    const headers = ["Well Name", "Operator", "Interests", "WI", "RI", "STR(s)"]
    // Mirror what's on screen: the CSV is an export of the current (filtered) view.
    const rows = filtered.map((w) => [
      w.well_name,
      w.operator_display || w.operator || "",
      (w.interests ?? []).join("; "),
      w.wi != null && w.wi !== "" ? String(w.wi) : "",
      w.ri != null && w.ri !== "" ? String(w.ri) : "",
      (w.strs ?? []).join("; "),
    ])
    // "Cherokee Moon - 7-19-2026.csv"
    const clientName = user?.client?.name || theme.clientName
    const date = new Date().toLocaleDateString("en-US").replace(/\//g, "-")
    downloadCsv(`${clientName} Units - ${date}.csv`, toCsv(headers, rows))
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }))

  const toggleInterest = (interest: string) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }))

  function resetForm() {
    setForm(blankForm)
    setEditingId(null)
    setShowForm(false)
    setValidationError(null)
  }

  function startAdd() {
    setForm(blankForm)
    setEditingId(null)
    setValidationError(null)
    saveMutation.reset()
    setShowForm(true)
  }

  function startEdit(well: Well) {
    setForm({
      well_name: well.well_name ?? "",
      operator: well.operator ?? "",
      strs: (well.strs ?? []).join(", "),
      wi: well.wi != null ? String(well.wi) : "",
      ri: well.ri != null ? String(well.ri) : "",
      interests: well.interests ?? [],
    })
    setEditingId(well.id)
    setValidationError(null)
    saveMutation.reset()
    setShowForm(true)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setValidationError(null)
    saveMutation.reset()

    if (!form.well_name.trim()) return setValidationError("Well name is required.")

    const payload = {
      well_name: form.well_name.trim(),
      operator: form.operator.trim() || null,
      wi: form.wi.trim() ? Number(form.wi) : null,
      ri: form.ri.trim() ? Number(form.ri) : null,
      interests: form.interests,
      strs: form.strs
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    }

    saveMutation.mutate(payload)
  }

  if (userLoading) {
    return (
      <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
        <div
          className="max-w-[1100px]"
          style={{ color: theme.textMuted, fontFamily: "var(--font-ui)" }}
        >
          Loading…
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <PageHeader
            title="Units & Wells"
            subtitle="Manage wells and units"
            theme={theme}
            icon={<Pickaxe size={17} strokeWidth={1.75} />}
          />
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => {
                importMutation.reset()
                importInputRef.current?.click()
              }}
              disabled={!canWrite || importMutation.isPending}
              className="btn"
              style={{
                ...secondaryButton(theme),
                display: "flex",
                alignItems: "center",
                gap: "6px",
                opacity: !canWrite || importMutation.isPending ? 0.6 : 1,
                cursor: !canWrite ? "default" : "pointer",
              }}
              title={canWrite ? undefined : "Viewers have read-only access"}
            >
              <Upload size={14} /> {importMutation.isPending ? "Importing…" : "Import CSV"}
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={!canWrite || filtered.length === 0}
              className="btn"
              style={{
                ...secondaryButton(theme),
                display: "flex",
                alignItems: "center",
                gap: "6px",
                opacity: !canWrite || filtered.length === 0 ? 0.6 : 1,
                cursor: !canWrite || filtered.length === 0 ? "default" : "pointer",
              }}
              title={
                !canWrite
                  ? "Viewers have read-only access"
                  : filtered.length === 0
                    ? "No units to export"
                    : "Export current view to CSV"
              }
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              type="button"
              onClick={startAdd}
              disabled={!canWrite}
              className="btn"
              style={{
                ...primaryButton(theme),
                opacity: canWrite ? 1 : 0.6,
                cursor: canWrite ? "pointer" : "default",
              }}
              title={canWrite ? undefined : "Viewers have read-only access"}
            >
              <Plus size={14} /> Add Unit
            </button>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => {
                handleImportFile(e.target.files?.[0])
                e.target.value = ""
              }}
            />
          </div>
        </div>

        {importMutation.isError && (
          <ErrorBanner theme={theme} message={formatApiError(importMutation.error)} />
        )}
        {importMutation.isSuccess && (
          <SuccessBanner
            theme={theme}
            message={`Imported wells: ${importMutation.data.created} created, ${importMutation.data.updated} updated${importMutation.data.errored.length ? `, ${importMutation.data.errored.length} skipped` : ""}.`}
          />
        )}

        {saveMutation.isSuccess && !showForm && (
          <SuccessBanner theme={theme} message="Unit saved." />
        )}
        {deleteMutation.isSuccess && <SuccessBanner theme={theme} message="Unit deleted." />}
        {deleteMutation.isError && (
          <ErrorBanner theme={theme} message={formatApiError(deleteMutation.error)} />
        )}

        <div
          className="rounded-lg border overflow-hidden mt-2"
          style={{
            borderColor: theme.borderColor,
            backgroundColor: theme.bgSurface,
          }}
        >
          <FilterBar
            hasFilters={!!search}
            onClear={() => setSearch("")}
            resultCount={filtered.length}
            totalCount={wells.length}
            entityLabel="unit"
            theme={theme}
          >
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search well, operator, STR…"
              theme={theme}
            />
          </FilterBar>

          {isMobile ? (
            isLoading ? (
              <div
                className="px-5 py-6"
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textMuted }}
              >
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div
                className="px-5 py-10 text-center"
                style={{ fontFamily: "var(--font-ui)", fontSize: "13px", color: theme.textMuted }}
              >
                No units found
              </div>
            ) : (
              <div className="flex flex-col gap-2.5 p-3">
                {filtered.map((well) => (
                  <DataCard
                    key={well.id}
                    title={well.well_name}
                    fields={[
                      { label: "Operator", value: well.operator_display || well.operator || "—" },
                      {
                        label: "Interests",
                        full: true,
                        value:
                          (well.interests ?? []).length === 0 ? (
                            "—"
                          ) : (
                            <div className="flex gap-1.5 flex-wrap">
                              {well.interests.map((i) => (
                                <span
                                  key={i}
                                  className="inline-block px-2 py-0.5 rounded-full"
                                  style={{
                                    fontFamily: "var(--font-ui)",
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    backgroundColor: `${theme.brandPrimary}18`,
                                    color: theme.brandPrimary,
                                  }}
                                >
                                  {i}
                                </span>
                              ))}
                            </div>
                          ),
                      },
                      {
                        label: "WI",
                        value: well.wi != null && well.wi !== "" ? String(well.wi) : "—",
                        mono: true,
                      },
                      {
                        label: "RI",
                        value: well.ri != null && well.ri !== "" ? String(well.ri) : "—",
                        mono: true,
                      },
                      {
                        label: "STR(s)",
                        full: true,
                        value: (
                          <StrChips
                            strs={well.strs ?? []}
                            inventoryStrs={inventoryStrs}
                            onOpen={openStr}
                            theme={theme}
                          />
                        ),
                      },
                    ]}
                    footer={
                      canWrite ? (
                        <>
                          <button
                            type="button"
                            onClick={() => startEdit(well)}
                            className="btn"
                            style={iconButton(theme)}
                          >
                            <Pencil size={13} /> Edit
                          </button>
                          {confirmDelete === well.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => deleteMutation.mutate(well.id)}
                                disabled={deleteMutation.isPending}
                                className="btn"
                                style={{ ...iconButton(theme), color: theme.brandAccent }}
                              >
                                Confirm
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDelete(null)}
                                className="btn"
                                style={iconButton(theme)}
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setConfirmDelete(well.id)
                                deleteMutation.reset()
                              }}
                              className="btn"
                              style={{ ...iconButton(theme), color: theme.brandAccent }}
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          )}
                        </>
                      ) : undefined
                    }
                  />
                ))}
              </div>
            )
          ) : (
            <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
              <table className="w-full border-collapse">
                <colgroup>
                  <col style={{ width: "26%" }} /> {/* Well Name — widest */}
                  <col style={{ width: "16%" }} /> {/* Operator */}
                  <col style={{ width: "10%" }} /> {/* Interests */}
                  <col style={{ width: "7%" }} /> {/* WI */}
                  <col style={{ width: "9%" }} /> {/* RI */}
                  <col style={{ width: canWrite ? "24%" : "32%" }} /> {/* STR(s) — wide */}
                  {canWrite && <col style={{ width: "8%" }} />} {/* Actions */}
                </colgroup>
                <thead>
                  <tr
                    style={{
                      backgroundColor: theme.bgSurface2,
                      borderBottom: `1px solid ${theme.borderColor}`,
                    }}
                  >
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Well Name" theme={theme} />
                    </th>
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Operator" theme={theme} />
                    </th>
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="Interests" theme={theme} />
                    </th>
                    <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                      <TableColumnHeader label="WI" theme={theme} />
                    </th>
                    <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                      <TableColumnHeader label="RI" theme={theme} />
                    </th>
                    <th className="px-3.5 py-2 text-left" style={stickyTh(theme)}>
                      <TableColumnHeader label="STR(s)" theme={theme} />
                    </th>
                    {canWrite && (
                      <th className="px-3.5 py-2 text-right" style={stickyTh(theme)}>
                        <TableColumnHeader label="Actions" theme={theme} />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan={canWrite ? 7 : 6}
                        className="px-5 py-6"
                        style={{
                          fontFamily: "var(--font-ui)",
                          fontSize: "13px",
                          color: theme.textMuted,
                        }}
                      >
                        Loading…
                      </td>
                    </tr>
                  ) : filtered.length === 0 ? (
                    <TableEmptyState
                      message="No units found"
                      onClear={() => setSearch("")}
                      colSpan={canWrite ? 7 : 6}
                      theme={theme}
                    />
                  ) : (
                    filtered.map((well) => (
                      <tr key={well.id} style={{ borderBottom: `1px solid ${theme.borderColor}` }}>
                        <td className="px-3.5 py-2" style={cellStyle(theme, true)}>
                          {well.well_name}
                        </td>
                        <td className="px-3.5 py-2" style={cellStyle(theme)}>
                          {well.operator_display || well.operator || "—"}
                        </td>
                        <td className="px-3.5 py-2">
                          <div className="flex gap-1.5 flex-wrap">
                            {(well.interests ?? []).length === 0 ? (
                              <span
                                style={{
                                  color: theme.textDim,
                                  fontFamily: "var(--font-ui)",
                                  fontSize: "12px",
                                }}
                              >
                                —
                              </span>
                            ) : (
                              well.interests.map((i) => (
                                <span
                                  key={i}
                                  className="inline-block px-2 py-0.5 rounded-full"
                                  style={{
                                    fontFamily: "var(--font-ui)",
                                    fontSize: "11px",
                                    fontWeight: 500,
                                    backgroundColor: `${theme.brandPrimary}18`,
                                    color: theme.brandPrimary,
                                  }}
                                >
                                  {i}
                                </span>
                              ))
                            )}
                          </div>
                        </td>
                        <td className="px-3.5 py-2 text-right" style={dataCellStyle(theme)}>
                          {well.wi != null && well.wi !== "" ? well.wi : "—"}
                        </td>
                        <td className="px-3.5 py-2 text-right" style={dataCellStyle(theme)}>
                          {well.ri != null && well.ri !== "" ? well.ri : "—"}
                        </td>
                        <td className="px-3.5 py-2">
                          <StrChips
                            strs={well.strs ?? []}
                            inventoryStrs={inventoryStrs}
                            onOpen={openStr}
                            theme={theme}
                          />
                        </td>
                        {canWrite && (
                          <td className="px-3.5 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => startEdit(well)}
                                className="btn"
                                style={iconButton(theme)}
                                title="Edit"
                              >
                                <Pencil size={13} />
                              </button>
                              {confirmDelete === well.id ? (
                                <span className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => deleteMutation.mutate(well.id)}
                                    disabled={deleteMutation.isPending}
                                    className="btn"
                                    style={{ ...iconButton(theme), color: theme.brandAccent }}
                                    title="Confirm delete"
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDelete(null)}
                                    className="btn"
                                    style={iconButton(theme)}
                                    title="Cancel"
                                  >
                                    Cancel
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setConfirmDelete(well.id)
                                    deleteMutation.reset()
                                  }}
                                  className="btn"
                                  style={{ ...iconButton(theme), color: theme.brandAccent }}
                                  title="Delete"
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <WellFormModal
          editing={!!editingId}
          form={form}
          set={set}
          toggleInterest={toggleInterest}
          onSubmit={handleSubmit}
          onClose={resetForm}
          saving={saveMutation.isPending}
          error={
            validationError ?? (saveMutation.isError ? formatApiError(saveMutation.error) : null)
          }
          theme={theme}
        />
      )}

      {activeStrRow && <StrDetailModal row={activeStrRow} onClose={() => setActiveStrRow(null)} />}
    </div>
  )
}

// Add/edit well form in a modal, following StrDetailModal's shell conventions
// (overlay + blur, ESC close, body scroll lock, full-screen on mobile). Owns
// no state — the page passes form state and handlers down.
function WellFormModal({
  editing,
  form,
  set,
  toggleInterest,
  onSubmit,
  onClose,
  saving,
  error,
  theme,
}: {
  editing: boolean
  form: FormState
  set: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  toggleInterest: (interest: string) => void
  onSubmit: (e: React.FormEvent) => void
  onClose: () => void
  saving: boolean
  error: string | null
  theme: ClientTheme
}) {
  const isMobile = useIsMobile()

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [])

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center ${isMobile ? "items-stretch" : "items-center p-4"}`}
      style={{ backgroundColor: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
      onClick={onClose}
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex flex-col overflow-y-auto ${isMobile ? "" : "rounded-2xl"}`}
        style={{
          backgroundColor: theme.bgSurface,
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          width: isMobile ? "100vw" : "min(680px, 95vw)",
          height: isMobile ? "100dvh" : undefined,
          maxHeight: isMobile ? undefined : "90vh",
        }}
      >
        <div
          className="flex items-center justify-between px-6 pt-5 pb-4"
          style={{ borderBottom: `1px solid ${theme.borderColor}` }}
        >
          <span
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "15px",
              fontWeight: 600,
              color: theme.textPrimary,
            }}
          >
            {editing ? "Edit Unit" : "Add Unit"}
          </span>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: theme.textMuted,
              padding: "4px",
            }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-4">
            <Field label="Well Name" theme={theme} required>
              <Input value={form.well_name} onChange={(v) => set("well_name", v)} theme={theme} />
            </Field>
            <Field label="Operator" theme={theme}>
              <Input value={form.operator} onChange={(v) => set("operator", v)} theme={theme} />
            </Field>
            <Field label="WI" theme={theme}>
              <Input
                type="number"
                step="any"
                value={form.wi}
                onChange={(v) => set("wi", v)}
                theme={theme}
              />
            </Field>
            <Field label="RI" theme={theme}>
              <Input
                type="number"
                step="any"
                value={form.ri}
                onChange={(v) => set("ri", v)}
                theme={theme}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="STR(s)" theme={theme}>
                <Input
                  placeholder="14-13N-19W, 15-13N-19W"
                  value={form.strs}
                  onChange={(v) => set("strs", v)}
                  theme={theme}
                />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <span style={labelStyle(theme)}>Interests</span>
              <div className="flex gap-4 mt-2">
                {INTEREST_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex items-center gap-2"
                    style={{ cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={form.interests.includes(opt)}
                      onChange={() => toggleInterest(opt)}
                    />
                    <span
                      style={{
                        fontFamily: "var(--font-ui)",
                        fontSize: "13px",
                        color: theme.textPrimary,
                      }}
                    >
                      {opt}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {error && <ErrorBanner theme={theme} message={error} />}
        </div>

        <div className="px-6 pb-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="btn w-full sm:w-auto"
            style={{ ...secondaryButton(theme), justifyContent: "center" }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn w-full sm:w-auto"
            style={{ ...primaryButton(theme), justifyContent: "center", opacity: saving ? 0.6 : 1 }}
          >
            {saving ? "Saving…" : editing ? "Save Changes" : "Add Unit"}
          </button>
        </div>
      </form>
    </div>
  )
}

// Clickable STR chips that open the STR summary popout. STRs held in Master
// Inventory render in the acquisition green shared via txColors; unowned STRs
// render greyed-out so ownership reads at a glance.
function StrChips({
  strs,
  inventoryStrs,
  onOpen,
  theme,
}: {
  strs: string[]
  inventoryStrs: Set<string>
  onOpen: (str: string) => void
  theme: ClientTheme
}) {
  if (strs.length === 0)
    return (
      <span style={{ color: theme.textDim, fontFamily: "var(--font-ui)", fontSize: "12px" }}>
        —
      </span>
    )
  return (
    <div className="flex gap-1.5 flex-wrap">
      {strs.map((s) => {
        const normalized = normalizeStr(s)
        const held = normalized !== null && inventoryStrs.has(normalized)
        const color = held ? ACQ_GREEN : theme.textMuted
        return (
          <button
            key={s}
            type="button"
            onClick={() => onOpen(s)}
            title={
              held
                ? "In master inventory — view STR summary"
                : "Not in master inventory — view STR summary"
            }
            className="inline-block px-2 py-0.5 rounded-md"
            style={{
              fontFamily: "var(--font-data)",
              fontSize: "12px",
              color,
              backgroundColor: held ? `${color}12` : "transparent",
              border: `1px solid ${color}30`,
              cursor: "pointer",
              opacity: held ? 1 : 0.7,
            }}
          >
            {s}
          </button>
        )
      })}
    </div>
  )
}

function cellStyle(theme: ClientTheme, bold = false): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "13px",
    fontWeight: bold ? 600 : 400,
    color: bold ? theme.textPrimary : theme.textMuted,
  }
}

function dataCellStyle(theme: ClientTheme): React.CSSProperties {
  return { fontFamily: "var(--font-data)", fontSize: "13px", color: theme.textPrimary }
}

function iconButton(theme: ClientTheme): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontFamily: "var(--font-ui)",
    fontSize: "12px",
    fontWeight: 500,
    padding: "5px 9px",
    borderRadius: "7px",
    backgroundColor: theme.bgSurface2,
    color: theme.textMuted,
    border: `1px solid ${theme.borderColor}`,
    cursor: "pointer",
  }
}
